import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import IconButton from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/IconButton';
import LinearProgress from 'Components/UI/LinearProgress';
import Uploader from 'Components/Files/MessageAttachments/Uploader';
import RoundButton from 'Components/UI/RoundButton';
import { getUploadText } from 'Helpers/File';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import { CircularProgress } from 'Components/UI/CircularProgress';
import { DataValueMap } from 'Helpers/Testing';

const styles = theme => ({
  root: {
    position: 'relative',
    marginRight: theme.spacing(1),
    height: '100%',
    display: 'flex',
    alignItems: 'center',
  },
  button: {},
  progressBar: {
    position: 'absolute',
    width: '120px',
    height: '100%',
    top: 0,
    right: 0,
    zIndex: 10, // Ensure this is above the button.
  },
  circularProgress: {},
});

function FileUploadButton({
  className,
  classes,
  messageResource,
  onFailure,
  onStart,
  onSuccess,
  setUploadProgress,
  uploadProgress,
  ButtonProps,
  ProgressProps,
  rounded,
  disabled,
  circularProgress,
}) {
  const isUploading = uploadProgress !== null;
  const disableButton = isUploading || disabled;
  const Progress = circularProgress ? CircularProgress : LinearProgress;
  const BaseProps = {
    value: uploadProgress,
    variant: 'determinate',
  };
  const CircularProgressProps = {
    size: 32,
    ...BaseProps,
    ...ProgressProps,
  };
  const ProgressBarProps = {
    style: { height: '100%' },
    borderRadius: 'xs',
    ...BaseProps,
    ...ProgressProps,
  };
  const ProgressComponentProps = circularProgress ? CircularProgressProps : ProgressBarProps;

  return (
    <div className={classNames(classes.root, className)}>
      <Uploader
        url={messageResource?.attr('attachments')?.link('self')?.url()}
        onStart={onStart}
        onSuccess={onSuccess}
        onCatch={onFailure}
        setUploadProgress={setUploadProgress}
        successToastMessage={null}
        disableClick={disableButton}
      >
        {(rounded) ? (
          <RoundButton
            className={classes.button}
            disabled={disableButton}
            variant="contained"
            color="accentBlue"
            disableElevation
            startIcon={<AttachFileIcon />}
            {...ButtonProps}
          >
            {'Attach File'}
          </RoundButton>
        ) : (
          <IconButton
            className={classes.button}
            disabled={isUploading}
            size="small"
            title={'Attach File'}
            icon={AttachFileIcon}
            {...ButtonProps}
            {...DataValueMap.init('file-upload-button').build()}
          />
        )}
      </Uploader>
      <Progress
        className={classNames({
          [classes.progressBar]: !circularProgress,
          [classes.circularProgress]: circularProgress,
        })}
        visible={isUploading}
        text={getUploadText(uploadProgress)}
        ProgressProps={ProgressComponentProps}
      />
    </div>
  );
}

FileUploadButton.defaultProps = {
  className: '',
  ButtonProps: {},
  ProgressProps: {},
  rounded: false,
  disabled: false,
  circularProgress: false,
};

FileUploadButton.propTypes = {
  classes: PropTypes.object.isRequired,
  className: PropTypes.string,
  messageResource: PropTypes.object.isRequired,
  onStart: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  onFailure: PropTypes.func.isRequired,
  ButtonProps: PropTypes.object,
  ProgressProps: PropTypes.object,
  rounded: PropTypes.bool,
  disabled: PropTypes.bool,
  circularProgress: PropTypes.bool,
};

export default withStyles(styles)(FileUploadButton);
